import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import {
  Home,
  Briefcase,
  Lightbulb,
  Search,
  GitCompare,
} from "lucide-react";
import { BottomNavigation } from "@/components/navigation";
import { OfflineBanner } from "@/components/ui/offline-banner";
import { GlobalErrorFallback, NotFoundError } from "@/components/error";
import { useOnlineStatus } from "@/hooks";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: GlobalErrorFallback,
  notFoundComponent: NotFoundError,
});

const navItems = [
  { to: "/", label: "Home", icon: Home },
  { to: "/holdings", label: "Holdings", icon: Briefcase },
  { to: "/recommendations", label: "Recommendations", icon: Lightbulb },
  { to: "/explore", label: "Explore", icon: Search },
  { to: "/compare", label: "Compare", icon: GitCompare },
] as const;

function RootLayout() {
  const { isOffline } = useOnlineStatus();

  return (
    <div className="min-h-screen bg-background">
      {/* Offline banner - shows when user loses connection */}
      <OfflineBanner show={isOffline} />

      {/* Desktop header - hidden on mobile */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:block hidden">
        <div className="container mx-auto flex h-14 items-center px-4">
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-xl font-bold text-foreground">
              Curie Compass
            </span>
          </Link>
          <nav className="ml-auto flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-accent [&.active]:text-accent-foreground"
              >
                <item.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Mobile header - visible on mobile only */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
        <div className="flex h-12 items-center justify-center px-4">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-bold text-foreground">
              Curie Compass
            </span>
          </Link>
        </div>
      </header>

      {/* Main content with bottom padding for mobile nav */}
      <main className="flex-1 pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Bottom navigation - mobile only */}
      <div className="md:hidden">
        <BottomNavigation />
      </div>
    </div>
  );
}
